import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';

// Async thunk to fetch the board by ID
export const fetchBoardById = createAsyncThunk(
    'board/fetchBoardById',
    async (boardId, { getState, rejectWithValue }) => {
        const state = getState();
        const _boardId = state.board.boardId || null;
        try {
            const response = await fetch(`${config.API_URI}/v1/boards/${boardId || _boardId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                // Parse error response
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message || 'Failed to fetch the board';

                return rejectWithValue({
                    status: response.status,
                    message: errorMessage,
                    type: response.status === 403 ? 'ACCESS_DENIED' :
                          response.status === 404 ? 'NOT_FOUND' : 'ERROR'
                });
            }

            const fetchedBoard = await response.json();
            return fetchedBoard || { actionLists: [] };
        } catch (error) {
            return rejectWithValue({
                status: 500,
                message: error.message || 'Failed to fetch the board',
                type: 'ERROR'
            });
        }
    }
);

// Async thunk to upload the background image and set it for the board
export const uploadBackgroundImage = createAsyncThunk(
    'board/uploadBackgroundImage',
    async ({ boardId, imageUrl }, { rejectWithValue }) => {
        
        try {
            const response = await fetch(`${config.API_URI}/v1/boards/boards-background`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ boardId, url: imageUrl }),
            });

            if (!response.ok) {
                throw new Error('Failed to upload background image');
            }

            return imageUrl; // Return the uploaded image URL
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const boardSlice = createSlice({
    name: 'board',
    initialState: {
        boardId: null,
        board: null,
        selectedBackgroundUrl: '',  // Temporary selected background image URL
        boardBackgroundUrl: '',  // Actual background image URL after upload
        isLoading: false,
        boardLoading: false,
        error: null,
    },
    reducers: {
        setBoardId: (state, action) => {
            state.boardId = action.payload;
        },
        setSelectedBackground: (state, action) => {
            state.selectedBackgroundUrl = action.payload;  // Optimistically set the selected background
        },
        setBoardBackground: (state, action) => {
            state.boardBackgroundUrl = action.payload;  // Set the actual background after upload
        },
        setLoading: (state, action) => {
            state.boardLoading = action.payload;  // Set loading state
        },
        resetBoard: (state) => {
            state.board = null;
            state.selectedBackgroundUrl = '';
            state.boardBackgroundUrl = '';
            state.isLoading = false;
            state.error = null;
        },

        // ========== OPTIMISTIC ACTIONS FOR REAL-TIME UPDATES ==========

        // Board updates
        optimisticUpdateBoard: (state, action) => {
            if (state.board) {
                // IMPORTANT: Exclude actionLists from updates to prevent overwriting populated lists with ObjectIds
                const { actionLists, ...safeUpdates } = action.payload;
                state.board = { ...state.board, ...safeUpdates };
            }
        },

        // Update board members (for real-time member changes)
        updateBoardMembers: (state, action) => {
            if (state.board) {
                state.board.members = action.payload;
            }
        },

        // List operations
        optimisticAddList: (state, action) => {
            if (state.board && state.board.actionLists) {
                const newList = { ...action.payload, cards: [] };
                state.board.actionLists.push(newList);
            }
        },

        optimisticUpdateList: (state, action) => {
            const { listId, updates } = action.payload;
            if (state.board && state.board.actionLists) {
                const listIndex = state.board.actionLists.findIndex(
                    list => list._id === listId || list.shortId === listId
                );
                if (listIndex !== -1) {
                    state.board.actionLists[listIndex] = {
                        ...state.board.actionLists[listIndex],
                        ...updates
                    };
                }
            }
        },

        optimisticDeleteList: (state, action) => {
            const listId = action.payload;
            if (state.board && state.board.actionLists) {
                state.board.actionLists = state.board.actionLists.filter(
                    list => list._id !== listId && list.shortId !== listId
                );
            }
        },

        optimisticReorderLists: (state, action) => {
            const orderMap = action.payload; // Array of { _id, order }
            if (state.board && state.board.actionLists) {
                const orderLookup = new Map(orderMap.map(({ _id, order }) => [_id, order]));

                // Create a new sorted array instead of mutating in place
                // This preserves all list data including cards
                state.board.actionLists = [...state.board.actionLists].sort((a, b) => {
                    const orderA = orderLookup.get(a._id) ?? a.order ?? 0;
                    const orderB = orderLookup.get(b._id) ?? b.order ?? 0;
                    return orderA - orderB;
                });
            }
        },

        // Card operations
        optimisticAddCard: (state, action) => {
            const { listId, card, position } = action.payload;
            if (state.board && state.board.actionLists) {
                const listIndex = state.board.actionLists.findIndex(
                    list => list._id === listId || list.shortId === listId
                );
                if (listIndex !== -1) {
                    const list = state.board.actionLists[listIndex];
                    if (!list.cards) list.cards = [];

                    // Check if card already exists (avoid duplicates)
                    const cardExists = list.cards.some(c =>
                        c._id === card._id || c.shortId === card.shortId
                    );

                    if (!cardExists) {
                        if (position === 'top') {
                            list.cards.unshift(card);
                        } else {
                            list.cards.push(card);
                        }

                        // Update card count
                        if (state.board.cardsCount !== undefined && state.board.cardsCount !== null) {
                            state.board.cardsCount += 1;
                        }
                    }
                }
            }
        },

        optimisticUpdateCard: (state, action) => {
            const { cardId, updates } = action.payload;
            if (state.board && state.board.actionLists) {
                for (let list of state.board.actionLists) {
                    if (list.cards) {
                        const cardIndex = list.cards.findIndex(
                            card => card._id === cardId || card.shortId === cardId
                        );
                        if (cardIndex !== -1) {
                            list.cards[cardIndex] = {
                                ...list.cards[cardIndex],
                                ...updates
                            };
                            break;
                        }
                    }
                }
            }
        },

        optimisticDeleteCard: (state, action) => {
            const cardId = action.payload;
            if (state.board && state.board.actionLists) {
                let cardDeleted = false;
                for (let list of state.board.actionLists) {
                    if (list.cards) {
                        const initialLength = list.cards.length;
                        list.cards = list.cards.filter(
                            card => card._id !== cardId && card.shortId !== cardId
                        );
                        if (list.cards.length < initialLength) {
                            cardDeleted = true;
                        }
                    }
                }

                // Update card count
                if (cardDeleted && state.board.cardsCount !== undefined && state.board.cardsCount !== null) {
                    state.board.cardsCount = Math.max(0, state.board.cardsCount - 1);
                }
            }
        },

        optimisticMoveCard: (state, action) => {
            const { cardId, sourceListId, targetListId, targetPosition } = action.payload;
            if (state.board && state.board.actionLists) {
                // Find source list and remove card
                let movedCard = null;
                const sourceList = state.board.actionLists.find(
                    list => list._id === sourceListId || list.shortId === sourceListId
                );

                if (sourceList && sourceList.cards) {
                    const cardIndex = sourceList.cards.findIndex(
                        card => card._id === cardId || card.shortId === cardId
                    );
                    if (cardIndex !== -1) {
                        movedCard = sourceList.cards[cardIndex];
                        sourceList.cards.splice(cardIndex, 1);
                    }
                }

                // Add card to target list
                if (movedCard) {
                    const targetList = state.board.actionLists.find(
                        list => list._id === targetListId || list.shortId === targetListId
                    );

                    if (targetList) {
                        if (!targetList.cards) targetList.cards = [];
                        targetList.cards.splice(targetPosition, 0, movedCard);
                    }
                }
            }
        },

        // Archive/Unarchive card
        optimisticArchiveCard: (state, action) => {
            const { cardId, archived } = action.payload;
            if (state.board && state.board.actionLists) {
                for (let list of state.board.actionLists) {
                    if (list.cards) {
                        const cardIndex = list.cards.findIndex(
                            card => card._id === cardId || card.shortId === cardId
                        );
                        if (cardIndex !== -1) {
                            if (archived) {
                                // Remove card from list when archived
                                list.cards.splice(cardIndex, 1);
                                // Update card count
                                if (state.board.cardsCount !== undefined && state.board.cardsCount !== null) {
                                    state.board.cardsCount = Math.max(0, state.board.cardsCount - 1);
                                }
                            } else {
                                // Update archived status when unarchived
                                list.cards[cardIndex].archived = archived;
                            }
                            break;
                        }
                    }
                }
            }
        },

        // Update card with full card data from socket
        optimisticUpdateCardFromSocket: (state, action) => {
            const updatedCard = action.payload;
            if (state.board && state.board.actionLists && updatedCard) {
                for (let list of state.board.actionLists) {
                    if (list.cards) {
                        const cardIndex = list.cards.findIndex(
                            card => card._id === updatedCard._id || card.shortId === updatedCard.shortId
                        );
                        if (cardIndex !== -1) {
                            // Replace entire card with updated data from socket
                            list.cards[cardIndex] = updatedCard;
                            break;
                        }
                    }
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBoardById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchBoardById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.board = action.payload;
            })
            .addCase(fetchBoardById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(uploadBackgroundImage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadBackgroundImage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.boardBackgroundUrl = action.payload;  // Save the uploaded background URL
                state.selectedBackgroundUrl = '';  // Clear the optimistically selected URL
            })
            .addCase(uploadBackgroundImage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    setBoardId,
    setSelectedBackground,
    setBoardBackground,
    setLoading,
    resetBoard,
    // Optimistic actions
    optimisticUpdateBoard,
    updateBoardMembers,
    optimisticAddList,
    optimisticUpdateList,
    optimisticDeleteList,
    optimisticReorderLists,
    optimisticAddCard,
    optimisticUpdateCard,
    optimisticDeleteCard,
    optimisticMoveCard,
    optimisticArchiveCard,
    optimisticUpdateCardFromSocket,
} = boardSlice.actions;

export default boardSlice.reducer;
